import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

const API_BASE_URL = 'https://xeniosdeals-backend.netplayer.workers.dev/api'

interface Property {
  id: number
  title: string
  description: string
  price_per_night: number
  city: string
  country: string
  is_active: boolean
  avg_rating: number
  review_count: number
  primary_image: string
}

interface HostDashboardProps {
  user: any
  onLogout: () => void
}

const HostDashboard: React.FC<HostDashboardProps> = ({ user, onLogout }) => {
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalProperties: 0,
    activeProperties: 0,
    totalBookings: 0,
    totalRevenue: 0
  })

  useEffect(() => {
    fetchHostData()
  }, [])

  const fetchHostData = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      
      // In a real app, you'd have specific endpoints for host data
      // For now, we'll simulate with existing endpoints
      const propertiesResponse = await fetch(`${API_BASE_URL}/properties`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      const propertiesData = await propertiesResponse.json()
      
      if (propertiesData.success) {
        // Filter properties by current host (in real app, backend would handle this)
        const hostProperties = propertiesData.data.filter((p: any) => 
          p.host_first_name === user.first_name && p.host_last_name === user.last_name
        )
        
        setProperties(hostProperties)
        setStats({
          totalProperties: hostProperties.length,
          activeProperties: hostProperties.filter((p: any) => p.is_active).length,
          totalBookings: 0, // Would come from bookings endpoint
          totalRevenue: 0   // Would come from bookings endpoint
        })
      }
    } catch (error) {
      console.error('Error fetching host data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    onLogout()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-red-600">XeniosDeals</h1>
              <span className="ml-4 text-sm text-gray-500">Host Dashboard</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {user.first_name}!</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Properties</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.totalProperties}</dd>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Active Properties</dt>
                <dd className="mt-1 text-3xl font-semibold text-green-600">{stats.activeProperties}</dd>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Bookings</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.totalBookings}</dd>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">${stats.totalRevenue}</dd>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <div className="flex space-x-4">
              <Link
                to="/host/properties/new"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
              >
                Add New Property
              </Link>
              <Link
                to="/host/bookings"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                View Bookings
              </Link>
              <Link
                to="/host/earnings"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Earnings
              </Link>
            </div>
          </div>

          {/* Properties List */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Your Properties</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Manage your property listings and view performance metrics.
              </p>
            </div>
            
            {properties.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2H7a2 2 0 012 2h10a2 2 0 012 2v16m-2 0a2 2 0 01-2 2H7a2 2 0 01-2-2m14 0V9a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2z" />
                  </svg>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No properties</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating your first property listing.
                </p>
                <div className="mt-6">
                  <Link
                    to="/host/properties/new"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                  >
                    Add Property
                  </Link>
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {properties.map((property) => (
                  <li key={property.id}>
                    <div className="px-4 py-4 flex items-center justify-between sm:px-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-16 w-16 bg-gray-300 rounded-lg overflow-hidden">
                          {property.primary_image ? (
                            <img
                              src={property.primary_image}
                              alt={property.title}
                              className="h-16 w-16 object-cover"
                            />
                          ) : (
                            <div className="h-16 w-16 bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-xs">No image</span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <h4 className="text-sm font-medium text-gray-900">{property.title}</h4>
                          <p className="text-sm text-gray-500">
                            {property.city}, {property.country}
                          </p>
                          <p className="text-sm text-gray-500">
                            ${property.price_per_night}/night
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          property.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {property.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Link
                          to={`/host/properties/${property.id}`}
                          className="text-red-600 hover:text-red-900 text-sm font-medium"
                        >
                          Manage
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HostDashboard
